import SelectionCell from "@js/components/tables/cells/SelectionCell.vue";
import SelectionHeaderCell from "@js/components/tables/cells/SelectionHeaderCell.vue";
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useVueTable,
} from "@tanstack/vue-table";
import { computed, h, onMounted, ref } from "vue";

export function getDefaultPageSize() {
    return 100;
}

export function getTenantDefaultPageSize() {
    return 100;
}

export const useDataTable = (options) => {
    const columnHelper = createColumnHelper();

    const fetchUserSession = ref(true);

    const loading = ref(true);

    const globalData = ref({});

    const data = ref([]);

    const count = ref(0);

    const sorting = ref([]);

    const pagination = ref({
        pageIndex: 0,
        pageSize: options.isTenantTable
            ? getTenantDefaultPageSize()
            : getDefaultPageSize(),
    });

    const pinning = ref(options.columnPinning ?? {});

    const pageCount = ref(0);

    const globalFilter = ref("");

    const availableFilters = ref([]);

    const filters = ref([]);

    const expanded = ref({});

    const selectionIds = ref([]);

    const allRowsSelected = ref(false);

    const columns = computed(() => {
        let columns = options.columns;

        if (options.selectionIdKey) {
            columns = [
                columnHelper.display({
                    id: "selection",
                    header: h(SelectionHeaderCell, {
                        selectAll: () => {
                            if (allRowsSelected.value) {
                                selectionIds.value = [];
                                allRowsSelected.value = false;
                            } else {
                                selectionIds.value = [
                                    ...data.value.map(
                                        (row) => row[options.selectionIdKey],
                                    ),
                                    "all",
                                ];
                                allRowsSelected.value = true;
                            }
                        },
                        selectAllVisible: () => {
                            allRowsSelected.value = false;

                            let allCurrentIds = data.value
                                .map((row) => row[options.selectionIdKey])
                                .filter((id) => id !== "all");

                            let allVisibleRowsSelected = allCurrentIds.every(
                                (id) => selectionIds.value.includes(id),
                            );

                            if (allVisibleRowsSelected) {
                                selectionIds.value = [
                                    ...selectionIds.value.filter(
                                        (id) =>
                                            !allCurrentIds.includes(id) &&
                                            id !== "all",
                                    ),
                                ];
                            } else {
                                let newValues = data.value
                                    .map((row) => row[options.selectionIdKey])
                                    .filter((id) => id !== "all");
                                selectionIds.value = [
                                    ...selectionIds.value.filter(
                                        (id) => !newValues.includes(id),
                                    ),
                                    ...newValues,
                                ];
                            }
                        },
                        selectionSize: () => {
                            if (allRowsSelected.value) {
                                return count.value;
                            } else {
                                return selectionIds.value.length;
                            }
                        },
                        align: "center",
                    }),
                    cell: (innerProps) =>
                        h(SelectionCell, {
                            row: innerProps.row,
                            selectionIds: selectionIds.value,
                            setSelectionIds: function (value) {
                                selectionIds.value = value;
                            },
                            idKey: options.selectionIdKey,
                            align: "center",
                        }),
                    width: 0,
                }),
                ...columns,
            ];
        }

        return columns;
    });

    const table = useVueTable({
        get data() {
            return data.value;
        },
        get pageCount() {
            return pageCount.value;
        },
        getSubRows: (row) => {
            return row.children || [];
        },
        columns: columns.value,
        state: {
            get columnPinning() {
                return pinning.value;
            },
            get pagination() {
                return pagination.value;
            },
            get sorting() {
                return sorting.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
            get expanded() {
                return expanded.value;
            },
        },
        onColumnPinningChange: (updaterOrValue) => {
            pinning.value =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(pinning.value)
                    : updaterOrValue;
        },
        onPaginationChange: (updaterOrValue) => {
            pagination.value =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(pagination.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onSortingChange: (updaterOrValue) => {
            sorting.value =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(sorting.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onGlobalFilterChange: (updaterOrValue) => {
            globalFilter.value =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(globalFilter.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onExpandedChange: (updaterOrValue) => {
            expanded.value =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(expanded.value)
                    : updaterOrValue;
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: getDefaultPageSize(),
            },
            columnVisibility: options.columnVisibility ?? {},
            columnPinning: options.columnPinning ?? {},
        },
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    });

    function setGlobalFilter(value) {
        globalFilter.value = value;
    }

    function setFilters(filters) {
        filters.value = filters;

        if (options.url) {
            updateData();
        } else {
            options.updateData();
        }
    }

    function updateData(callback = null) {
        if (options.url) {
            loading.value = true;

            axios
                .post(options.url, {
                    pageIndex: pagination.value.pageIndex,
                    pageSize: pagination.value.pageSize,
                    sorting: sorting.value,
                    globalFilter: globalFilter.value,
                    filters: filters.value,
                    fetchUserSession: fetchUserSession.value,
                    userSessionKey: options.userSessionKey,
                    data:
                        options.data && options.data.value
                            ? options.data.value
                            : options.data,
                })
                .then((response) => {
                    data.value = response.data.data;
                    count.value = response.data.count;
                    pagination.value.pageIndex = response.data.pageIndex;
                    pagination.value.pageSize = response.data.pageSize;
                    pageCount.value = response.data.pageCount;
                    sorting.value = response.data.sorting;
                    globalFilter.value = response.data.globalFilter;
                    filters.value = response.data.filters;
                    availableFilters.value = response.data.availableFilters;
                    globalData.value = response.data.globalData;
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    loading.value = false;

                    if (callback) {
                        callback();
                    }
                });
        } else {
            throw new Error("No URL provided for data table");
        }
    }

    function exportData(format = "csv") {
        if (options.exportUrl && options.selectionIdKey) {
            loading.value = true;

            let url = new URL(options.exportUrl);
            url.searchParams.append("format", format);

            axios
                .post(
                    url.toString(),
                    {
                        selectionIds: selectionIds.value,
                        sorting: sorting.value,
                        globalFilter: globalFilter.value,
                        filters: filters.value,
                        selectionIdKey:
                            options.exportSelectionIdKey ??
                            options.selectionIdKey,
                        data:
                            options.data && options.data.value
                                ? options.data.value
                                : options.data,
                    },
                    { responseType: "blob" },
                )
                .then((response) => {
                    const contentDisposition =
                        response.headers["content-disposition"];

                    if (contentDisposition != null) {
                        let filename = contentDisposition
                            .split(";")[1]
                            .split("=")[1]
                            .replace('"', "")
                            .replace('"', "");
                        filename = decodeURI(filename);

                        const url = window.URL.createObjectURL(
                            new Blob([response.data]),
                        );
                        const link = document.createElement("a");

                        link.href = url;
                        link.setAttribute("download", filename);

                        document.body.appendChild(link);
                        link.click();

                        window.URL.revokeObjectURL(url);
                        link.remove();
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    loading.value = false;
                });
        } else {
            throw new Error(
                "No export URL provided for data table or no selection ID key",
            );
        }
    }

    onMounted(() => {
        if (options.baseRequest?.pageSize) {
            pagination.value.pageSize = options.baseRequest.pageSize;
        }

        if (options.baseRequest?.sorting) {
            sorting.value = options.baseRequest.sorting;
        }

        if (options.baseRequest?.globalFilter) {
            globalFilter.value = options.baseRequest.globalFilter;
        }

        if (options.baseRequest?.availableFilters) {
            availableFilters.value = options.baseRequest.availableFilters;
        }

        if (options.baseRequest?.filters) {
            filters.value = options.baseRequest.filters;
        }

        if (options.url) {
            updateData(() => {
                fetchUserSession.value = false;
            });
        } else {
            options.updateData(() => {
                fetchUserSession.value = false;
            });
        }
    });

    return {
        fetchUserSession,
        loading,
        table,
        globalData,
        data,
        count,
        pagination,
        pageCount,
        sorting,
        globalFilter,
        setGlobalFilter,
        availableFilters,
        filters,
        setFilters,
        selectionIds,
        updateData,
        exportData,
    };
};
